import { useCallback, useState } from "react";
import {
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Divider,
  Typography,
  TextField,
  Box,
  InputAdornment,
} from "@mui/material";
import {
  TableChart as TableIcon,
  BorderClear as TransparentTableIcon,
  Warning as WarningTableIcon,
  TableView as DefaultTableIcon,
  Percent as PercentIcon,
  Height as HeightIcon,
} from "@mui/icons-material";
import { useRichTextEditorContext, MenuButtonProps } from "mui-tiptap";
import { useTranslation } from "react-i18next";

interface TableStyleOption {
  name: string;
  icon: React.ReactNode;
  value: string | null;
  description: string;
}

const tableStyleOptions: TableStyleOption[] = [
  {
    name: "defaultTable",
    icon: <DefaultTableIcon />,
    value: null,
    description: "Default table style",
  },
  {
    name: "transparentTable",
    icon: <TransparentTableIcon />,
    value: "transparent",
    description: "Transparent table (no borders)",
  },
  {
    name: "warningTable",
    icon: <WarningTableIcon />,
    value: "warning",
    description: "Warning table (orange borders)",
  },
];

export interface MenuButtonTableStylesProps extends Partial<MenuButtonProps> {
  tooltipLabel?: string;
  "aria-label"?: string;
}

export default function MenuButtonTableStyles({
  tooltipLabel = "Table Styles",
  "aria-label": ariaLabel,
}: MenuButtonTableStylesProps) {
  const { t } = useTranslation();
  const editor = useRichTextEditorContext();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [widthInput, setWidthInput] = useState<string>("");
  const [heightInput, setHeightInput] = useState<string>("");
  const open = Boolean(anchorEl);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    },
    []
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const applyTableStyle = useCallback(
    (styleValue: string | null) => {
      if (!editor) return;

      if (styleValue === null) {
        editor.chain().focus().clearTableStyles().run();
      } else if (styleValue === "transparent") {
        editor.chain().focus().setTransparentTable().run();
      } else if (styleValue === "warning") {
        editor.chain().focus().setWarningTable().run();
      }

      handleClose();
    },
    [editor, handleClose]
  );

  const applyExactWidth = useCallback(
    (percentValue: number) => {
      if (!editor || percentValue < 1 || percentValue > 100) return;

      editor.chain().focus().setTableWidth(`${percentValue}%`).run();
      handleClose();
    },
    [editor, handleClose]
  );

  const applyWidthFromInput = useCallback(() => {
    if (!editor || !widthInput) return;

    const numValue = parseInt(widthInput);
    if (numValue >= 1 && numValue <= 100) {
      editor.chain().focus().setTableWidth(`${numValue}%`).run();
      setWidthInput("");
      handleClose();
    }
  }, [editor, widthInput, handleClose]);

  const handleWidthInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      if (value === "" || (/^\d+$/.test(value) && parseInt(value) <= 100)) {
        setWidthInput(value);
      }
    },
    []
  );

  const handleWidthSubmit = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter") {
        const numValue = parseInt(widthInput);
        if (numValue >= 1 && numValue <= 100) {
          applyExactWidth(numValue);
          setWidthInput("");
        }
      }
    },
    [widthInput, applyExactWidth]
  );

  const handleHeightInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;

      if (value === "" || (/^\d+$/.test(value) && parseInt(value) <= 1000)) {
        setHeightInput(value);
      }
    },
    []
  );

  const applyCellHeight = useCallback(() => {
    if (!editor || !heightInput) return;

    const height = parseInt(heightInput, 10);
    if (isNaN(height) || height < 25) return;

    editor.chain().focus().setCellHeight(height).run();
    setHeightInput("");
    handleClose();
  }, [editor, heightInput, handleClose]);

  const handleHeightSubmit = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter") {
        event.preventDefault();
        applyCellHeight();
      }
    },
    [applyCellHeight]
  );

  const getCurrentStyle = useCallback(() => {
    if (!editor) return null;

    const tableAttributes = editor.getAttributes("table");
    return tableAttributes["data-table-style"] || null;
  }, [editor]);

  const getCurrentWidth = useCallback(() => {
    if (!editor) return null;

    const tableAttributes = editor.getAttributes("table");
    const width = tableAttributes["data-table-width"];
    if (width) {
      return width;
    }
    return null;
  }, [editor]);

  if (!editor) {
    return null;
  }

  const isInTable = editor.isActive("table");
  const currentStyle = getCurrentStyle();
  const currentWidth = getCurrentWidth();

  const currentStyleOption = tableStyleOptions.find(
    (option) => option.value === currentStyle
  );
  const ButtonIcon = currentStyleOption ? (
    currentStyleOption.icon
  ) : (
    <TableIcon />
  );

  return (
    <>
      <Tooltip title={tooltipLabel}>
        <span>
          <Button
            aria-label={ariaLabel}
            disabled={!editor?.isEditable || !isInTable}
            onClick={handleClick}
            size="small"
            variant="text"
            sx={{
              minWidth: "auto",
              padding: "4px 8px",
              color: "text.primary",
              "&:hover": {
                backgroundColor: "action.hover",
              },
            }}
          >
            {ButtonIcon}
          </Button>
        </span>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        {/* Table Style Options */}
        <Typography
          variant="subtitle2"
          sx={{ px: 2, py: 1, fontWeight: "bold", color: "text.secondary" }}
        >
          {t("tableStyle", "Table Style")}
        </Typography>
        {tableStyleOptions.map((option) => (
          <MenuItem
            key={option.name}
            onClick={() => applyTableStyle(option.value)}
            selected={currentStyle === option.value}
            sx={{ minWidth: 200 }}
          >
            <ListItemIcon>{option.icon}</ListItemIcon>
            <ListItemText
              primary={t(option.name, option.description)}
              secondary={
                currentStyle === option.value ? t("current", "Current") : ""
              }
            />
          </MenuItem>
        ))}

        <Divider sx={{ my: 1 }} />
        <Typography
          variant="subtitle2"
          sx={{ px: 2, py: 1, fontWeight: "bold", color: "text.secondary" }}
        >
          {t("tableWidth", "Table Width")}
        </Typography>
        <Box sx={{ px: 2, pb: 2 }}>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Box sx={{ flex: "2 1 0%" }}>
              <TextField
                fullWidth
                size="small"
                label={t("exactWidth", "Exact Width")}
                value={widthInput}
                onChange={handleWidthInputChange}
                onKeyDown={handleWidthSubmit}
                placeholder={currentWidth || "Auto"}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <PercentIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Box>
            <Box sx={{ flex: "1 1 0%" }}>
              <Button
                fullWidth
                variant="outlined"
                size="small"
                onClick={applyWidthFromInput}
                disabled={!widthInput}
              >
                {t("apply", "Apply")}
              </Button>
            </Box>
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 0.5, display: "block" }}
          >
            {t("widthHelperText", "Enter 1-100% and press Enter")}
          </Typography>
        </Box>

        <Divider sx={{ my: 1 }} />
        <Typography
          variant="subtitle2"
          sx={{
            px: 2,
            py: 1,
            fontWeight: "bold",
            color: "text.secondary",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <HeightIcon fontSize="small" />
          {t("cellHeight", "Current Cell Height")}
        </Typography>
        <Box sx={{ px: 2, pb: 2 }}>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Box sx={{ flex: "2 1 0%" }}>
              <TextField
                fullWidth
                size="small"
                value={heightInput}
                onChange={handleHeightInputChange}
                onKeyDown={handleHeightSubmit}
                placeholder="40"
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <Typography variant="caption" color="text.secondary">
                          px
                        </Typography>
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Box>
            <Box sx={{ flex: "1 1 0%" }}>
              <Button
                fullWidth
                variant="outlined"
                size="small"
                onClick={applyCellHeight}
                disabled={!heightInput}
              >
                {t("apply", "Apply")}
              </Button>
            </Box>
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 0.5, display: "block" }}
          >
            {t(
              "cellHeightHelp",
              "Set height for the currently selected cell. Min height: 25px"
            )}
          </Typography>
        </Box>
      </Menu>
    </>
  );
}
